import { type SettingsConfig, SettingsType } from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import { DropdownOptionType } from '@ardoq/dropdown-menu';
import type { ViewIds } from '@ardoq/api-types';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { BlocksLinksAggregation } from 'tabview/visualizations/aq-blocks/types';
import { handleStoreSetting } from 'viewSettings/settingsHelper';

export type ReferenceLineSettings = {
  separateReferences: boolean;
  /** Takes precedence over `separateReferences` when true. */
  hideReferences: boolean;
};

export const toLinksAggregation = ({
  separateReferences,
  hideReferences,
}: ReferenceLineSettings): BlocksLinksAggregation => {
  if (hideReferences) return 'hide';
  return separateReferences ? 'separate' : 'byReferenceType';
};

/** Combine, separate or hide an aq-blocks view's reference lines. */
export const referenceLinesDropdown = (
  viewId: ViewIds,
  viewSettings: ReferenceLineSettings,
  i18nLocale: I18nLocale
): SettingsConfig => ({
  id: 'referenceLines',
  type: SettingsType.DROPDOWN,
  iconName: IconName.SEPARATE_REFERENCES,
  label: t(
    i18nLocale,
    'appLayout:viewPaneHeader.referenceLines',
    'Reference lines'
  ),
  options: [
    {
      name: 'reference_lines_combine',
      label: t(
        i18nLocale,
        'appLayout:viewPaneHeader.combineReferenceLines',
        'Combine reference lines'
      ),
      type: DropdownOptionType.OPTION,
      isActive:
        !viewSettings.hideReferences && !viewSettings.separateReferences,
      onClick: () =>
        handleStoreSetting(viewId, {
          separateReferences: false,
          hideReferences: false,
        }),
    },
    {
      name: 'reference_lines_separate',
      label: t(
        i18nLocale,
        'appLayout:viewPaneHeader.separateReferenceLines',
        'Separate reference lines'
      ),
      type: DropdownOptionType.OPTION,
      isActive: !viewSettings.hideReferences && viewSettings.separateReferences,
      onClick: () =>
        handleStoreSetting(viewId, {
          separateReferences: true,
          hideReferences: false,
        }),
    },
    {
      name: 'reference_lines_hide',
      label: t(
        i18nLocale,
        'appLayout:viewPaneHeader.hideReferenceLines',
        'Hide reference lines'
      ),
      type: DropdownOptionType.OPTION,
      isActive: viewSettings.hideReferences,
      onClick: () => handleStoreSetting(viewId, { hideReferences: true }),
    },
  ],
  dropdownSize: DropdownSize.S,
});
